import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGLineChart, PGBarChart } from '../../components/primitives/PGChart';
import { GROUP, REGIONS, BRANCHES } from '../../utils/orgData';
import { PG } from '../../utils/theme';
import { useLiveFeed } from '../../components/live/LiveFeedProvider';
import SplitFlap from '../../components/live/SplitFlap';
import Sparkline from '../../components/live/Sparkline';

const { FiPackage, FiAlertCircle, FiRepeat, FiTruck, FiMinusCircle } = FiIcons;

const CRITICAL_LOW = [
  { sku: 'WS-TOY-HLX-F', desc: 'Toyota Hilux front ws', qty: 2, branch: 'Pietermaritzburg', branchId: 'pietermaritzburg', tone: 'alert' },
  { sku: 'WS-FRD-RGR-F', desc: 'Ford Ranger front ws', qty: 1, branch: 'Centurion', branchId: 'centurion', tone: 'alert' },
  { sku: 'WS-VW-POLO-F', desc: 'VW Polo front ws', qty: 3, branch: 'East London', branchId: 'east-london', tone: 'warn' },
  { sku: 'WS-MZD-BT50-R', desc: 'Mazda BT-50 rear ws', qty: 0, branch: 'Maputo', branchId: 'maputo', tone: 'alert' },
  { sku: 'WS-BMW-3S-F', desc: 'BMW 3-Series front ws', qty: 2, branch: 'Sandton CBD', branchId: 'sandton-cbd', tone: 'warn' },
  { sku: 'WS-ISU-DMX-F', desc: 'Isuzu D-Max front ws', qty: 1, branch: 'Gqeberha', branchId: 'port-elizabeth', tone: 'alert' },
  { sku: 'ADH-URETH-310', desc: 'Urethane adhesive 310ml', qty: 8, branch: 'Harare', branchId: 'harare', tone: 'warn' },
  { sku: 'PRMR-GL-250', desc: 'Glass primer 250ml', qty: 4, branch: 'Lusaka', branchId: 'lusaka', tone: 'warn' },
];

const SUPPLIERS = [
  { name: 'PGSA Dist', difot: 97 },
  { name: 'AIS Glass', difot: 94 },
  { name: 'Shatterprufe', difot: 92 },
  { name: 'FY Africa', difot: 88 },
  { name: 'XYG SA', difot: 86 },
  { name: 'Local OEM', difot: 79 },
];

function stockoutSeries(seed = 3) {
  const out = [];
  let v = 6;
  for (let i = 0; i < 12; i++) {
    v = Math.max(0, Math.round(v + Math.sin(i * 0.6 + seed) * 3));
    out.push(v);
  }
  return out;
}

const FlashRow = ({ watchValue, children, className = '', onClick }) => {
  const prev = useRef(watchValue);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (prev.current !== watchValue) {
      setFlash(true);
      prev.current = watchValue;
      const t = setTimeout(() => setFlash(false), 3000);
      return () => clearTimeout(t);
    }
  }, [watchValue]);
  return (
    <tr className={`${className} transition-shadow duration-700`} style={flash ? { boxShadow: '0 0 24px rgba(255,184,0,0.25)', background: 'rgba(255,184,0,0.04)' } : {}} onClick={onClick}>
      {children}
    </tr>
  );
};

export default function SupplySliver() {
  const s = GROUP.kpis.supply;

  const feed = useLiveFeed();
  const liveStockouts = feed.skuStockLow;

  const regionAvail = REGIONS.map((r) => ({
    name: r.name,
    val: +(r.branches.reduce((acc, b) => acc + b.supply.stockAvailability, 0) / r.branches.length).toFixed(1),
  })).sort((a, b) => a.val - b.val);

  const trend = stockoutSeries();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard
          title="STOCK AVAILABILITY"
          value={s.stockAvailability}
          unit="%"
          icon={FiPackage}
          accent={PG.emerald}
          target=">95%"
          animated="ticker"
          numericValue={Math.round(s.stockAvailability * 10)}
          tickerDigits={4}
          tickerSuffix="%"
          badge={liveStockouts > 0 ? (
            <span className="flex items-center gap-1 border border-[#D71921]/60 bg-[#D71921]/10 px-1.5 py-0.5 text-[#D71921] font-mono-tech text-[9px] uppercase tracking-widest animate-pulse">
              +<SplitFlap text={String(liveStockouts).padStart(2, '0')} width={2} />
            </span>
          ) : null}
        />
        <KPICard
          title="STOCKOUT EVENTS"
          value={s.stockoutEvents}
          icon={FiAlertCircle}
          accent={PG.red}
          target="<20"
          animated="splitflap"
          flapText={String(s.stockoutEvents).padStart(2, '0')}
          flapWidth={2}
        />
        <KPICard title="STOCK TURN DAYS" value={s.stockTurnDays} unit="days" icon={FiRepeat} accent={PG.amber} target="<20" />
        <KPICard title="DIFOT SUPPLIERS" value={s.difotSuppliers} unit="%" icon={FiTruck} accent={PG.cyan} target=">95%" />
        <KPICard
          title="SHRINKAGE"
          value={s.shrinkagePct}
          unit="%"
          icon={FiMinusCircle}
          accent={PG.amber}
          target="<1%"
          sparkValues={feed.wipAgeP90Mins}
        />
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <div className="col-span-5 flex flex-col min-h-0">
          <Panel title="STOCK_AVAILABILITY_BY_REGION" subtitle="worst → best">
            <div className="h-full p-2">
              <PGBarChart
                horizontal
                categories={regionAvail.map((r) => r.name).reverse()}
                series={[
                  {
                    name: 'Availability %',
                    data: regionAvail
                      .map((r) => ({
                        value: r.val,
                        itemStyle: { color: r.val < 92 ? PG.red : r.val < 96 ? PG.amber : PG.emerald },
                      }))
                      .reverse(),
                  },
                ]}
                yFormatter={(v) => v + '%'}
              />
            </div>
          </Panel>
          <div className="mt-2 grid grid-cols-5 gap-2 px-3 flex-shrink-0">
            {REGIONS.slice(0, 5).map((r) => {
              const offset = r.id.charCodeAt(0) % 20;
              const slice = feed.throughputPerMin.slice(offset % 5);
              return (
                <div key={r.id} className="flex items-center gap-2 font-mono-tech text-[8px] text-neutral-500 uppercase tracking-wider">
                  <span className="truncate flex-1">{r.name}</span>
                  <div className="w-12 h-[10px] text-[#FFB800]">
                    <Sparkline values={slice} width={48} height={10} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Panel title="CRITICAL_LOW_ITEMS" subtitle={`${CRITICAL_LOW.length} SKUs flagged`} tone="alert" className="col-span-7">
          <div className="h-full overflow-y-auto custom-scrollbar">
            <table className="w-full text-xs">
              <thead className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 bg-black/40 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">SKU</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-left px-3 py-2">Branch</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-center px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {CRITICAL_LOW.map((item, i) => (
                  <FlashRow
                    key={i}
                    watchValue={liveStockouts + (item.sku || item.id || '').toString().charCodeAt(0)}
                    className="row-clickable cursor-pointer border-t border-white/5 hover:bg-[#D71921]/10 transition-colors"
                    onClick={() => { window.location.hash = `#/sliver/supply/branch/${item.branchId}`; }}
                  >
                    <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-400">{item.sku}</td>
                    <td className="px-3 py-2 text-neutral-300">{item.desc}</td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/sliver/supply/branch/${item.branchId}`}
                        className="text-white hover:text-[#D71921] font-medium"
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        {item.branch}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right font-doto text-lg" style={{ color: item.qty === 0 ? PG.red : PG.white }}>{item.qty}</td>
                    <td className="px-3 py-2 text-center"><StatusPill tone={item.tone}>{item.qty === 0 ? 'STOCKOUT' : 'LOW'}</StatusPill></td>
                  </FlashRow>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="SUPPLIER_DIFOT_SCORECARD" subtitle="delivery in-full / on-time" className="col-span-7">
          <div className="h-full p-2">
            <PGBarChart
              categories={SUPPLIERS.map((s) => s.name)}
              series={[
                {
                  name: 'DIFOT %',
                  data: SUPPLIERS.map((s) => ({
                    value: s.difot,
                    itemStyle: { color: s.difot >= 95 ? PG.emerald : s.difot >= 90 ? PG.amber : PG.red },
                  })),
                },
              ]}
              yFormatter={(v) => v + '%'}
            />
          </div>
        </Panel>

        <Panel title="12_WK_STOCKOUT_TREND" subtitle="group — events per week" className="col-span-5" livePulse>
          <div className="h-full p-2">
            <PGLineChart
              categories={Array.from({ length: 12 }, (_, i) => `W${i + 1}`)}
              series={[{ name: 'Stockouts', data: trend, itemStyle: { color: PG.red }, lineStyle: { color: PG.red, width: 2 } }]}
            />
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
